import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { fetchActingOrg, type MeAdminOrg } from '@/api/orgAdmin';
import { useSessionStore } from '@/store/sessionStore';
import { ActingOrgContext } from '@/auth/actingOrgContext';

/**
 * EAA-1846: the default when nothing is persisted yet. `organizations` is
 * ordered alphabetically by name (server-side, for the picker's own sake — a
 * super-admin sees every org in the system), NOT by any notion of "the org to
 * land in by default" — so `organizations[0]` used to be whichever org
 * happened to sort first. A freshly created org named e.g. "E2E Unit …" sorts
 * before "EMG" and silently became every fresh session's default console
 * scope, including the super-admin's. Prefer a root org (`parent_id === null`)
 * first; only an org-admin scoped to a single non-root org has none, so
 * falling back to the array's own first entry stays correct for them.
 */
function pickDefaultOrg(organizations: MeAdminOrg[]): MeAdminOrg | undefined {
    return organizations.find((o) => o.parent_id === null) ?? organizations[0];
}

/**
 * EAA-1797 (P4b): provides the console's acting organization.
 *
 * `organizations` (from `/me/admin`) is the authoritative list — only orgs the
 * caller may administer appear there. The persisted per-tab choice is honored
 * when it is still one of those orgs, otherwise we default via `pickDefaultOrg`
 * (EAA-1846: root-preferring, not just the first entry). The effective choice
 * is validated against `GET /me/acting-org` (P4a, EAA-1796) as defense in
 * depth — that catches an org deactivated after `/me/admin` was cached — and
 * on rejection we drop to the same default. Switching org invalidates the
 * console's data queries so they refetch carrying the new `X-Org-Id`.
 */
export function ActingOrgProvider({
    organizations,
    children,
}: {
    organizations: MeAdminOrg[];
    children: ReactNode;
}) {
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const actingOrgId = useSessionStore((s) => s.actingOrgId);
    const setActingOrgId = useSessionStore((s) => s.setActingOrgId);

    const validIds = useMemo(() => organizations.map((o) => o.id), [organizations]);
    const effectiveId =
        actingOrgId && validIds.includes(actingOrgId)
            ? actingOrgId
            : pickDefaultOrg(organizations)?.id ?? null;

    // Converge the store (and sessionStorage) onto the effective choice so the api
    // layer sends the right X-Org-Id even before the user touches the picker.
    useEffect(() => {
        if (effectiveId !== actingOrgId) {
            setActingOrgId(effectiveId);
        }
    }, [effectiveId, actingOrgId, setActingOrgId]);

    const validation = useQuery({
        queryKey: ['acting-org', accessToken, effectiveId],
        queryFn: () => fetchActingOrg(config.apiBase, accessToken as string, effectiveId as string),
        enabled: Boolean(accessToken && effectiveId),
        retry: false,
    });
    useEffect(() => {
        const fallback = pickDefaultOrg(organizations)?.id ?? null;
        if (validation.isError && fallback && effectiveId !== fallback) {
            setActingOrgId(fallback);
        }
    }, [validation.isError, organizations, effectiveId, setActingOrgId]);

    const setActingOrg = useCallback(
        (orgId: string) => {
            setActingOrgId(orgId);
            // Refetch the console's data queries with the new header; leave the
            // identity queries (me-admin / acting-org validation) alone to avoid a
            // refetch loop.
            void queryClient.invalidateQueries({
                predicate: (q) => {
                    const head = q.queryKey[0];
                    return head !== 'me-admin' && head !== 'acting-org';
                },
            });
        },
        [setActingOrgId, queryClient]
    );

    const value = useMemo(
        () => ({
            organizations,
            actingOrgId: effectiveId,
            actingOrg: organizations.find((o) => o.id === effectiveId) ?? null,
            setActingOrg,
            canSwitch: organizations.length >= 2,
        }),
        [organizations, effectiveId, setActingOrg]
    );

    return <ActingOrgContext.Provider value={value}>{children}</ActingOrgContext.Provider>;
}
